import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import DateInput from '@/components/ui/date-input';
import { formatDisplayValue, formatTime } from '@/lib/utils';
import { toDateInputValue } from '../utils';
import { searchInputClassName } from '../enrollmentSearchCriteria';
import { FieldSuggestDropdown } from './FieldSearchSuggest';

/**
 * Form field that toggles between a read-only display and an <Input>.
 * For type="date" values are normalised to YYYY-MM-DD via toDateInputValue.
 * Optional suggestItems: Autocomplete-Dropdown (Suchmodus).
 */
export const FormField = ({
  label,
  value,
  className = '',
  editable = false,
  onChange,
  type = 'text',
  placeholder,
  suggestItems,
  suggestLoading,
  onSuggestSelect,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  ...rest
}) => {
  const displayValue = type === 'date'
    ? toDateInputValue(value)
    : type === 'time'
      ? (formatTime(value) || '')
      : (value || '');
  const searching = editable && rest['data-testid']?.startsWith?.('search-field-');
  const [highlight, setHighlight] = useState(0);
  const hasSuggest = searching && (suggestLoading || (suggestItems && suggestItems.length > 0));

  const pick = (item, { submit = false } = {}) => {
    if (!item) return;
    onSuggestSelect?.(item, { submit });
    setHighlight(0);
  };

  const onKeyDown = (e) => {
    rest.onKeyDown?.(e);
    if (!hasSuggest || !suggestItems?.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      setHighlight((h) => Math.min(h + 1, suggestItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && suggestItems.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      pick(suggestItems[highlight] || suggestItems[0], { submit: true });
    } else if (e.key === 'Escape') {
      e.stopPropagation();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <label className="text-[10px] text-slate-500 uppercase tracking-wide leading-none block mb-0.5">
        {label}
      </label>
      {editable ? (
        type === 'date' ? (
          <DateInput
            value={displayValue}
            onChange={(e) => onChange(e.target.value)}
            className={`text-xs h-7 font-medium px-2 ${searching ? searchInputClassName(displayValue) : ''}`}
            {...rest}
          />
        ) : (
          <>
            <Input
              type={type}
              value={displayValue}
              placeholder={placeholder}
              autoComplete="off"
              className={`text-xs h-6 font-medium px-2 ${searching ? searchInputClassName(displayValue) : ''}`}
              {...rest}
              onChange={(e) => {
                setHighlight(0);
                onChange(e.target.value);
              }}
              onFocus={(e) => onFocusProp?.(e)}
              onBlur={(e) => onBlurProp?.(e)}
              onKeyDown={onKeyDown}
            />
            {hasSuggest && (
              <FieldSuggestDropdown
                items={suggestItems || []}
                loading={suggestLoading}
                highlight={highlight}
                onHighlight={setHighlight}
                onSelect={pick}
              />
            )}
          </>
        )
      ) : (
        <div
          className="text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded px-2 py-1 min-h-[24px] leading-tight"
          data-testid={rest['data-testid']}
        >
          {formatDisplayValue(value, type) || '-'}
        </div>
      )}
    </div>
  );
};

export default FormField;
